import Phaser from "phaser";
import { TILESET_NAME, TILESET_KEY } from "../tiles";
import { renderTiledAuthoredContent } from "../ui/tiledRender";
import { makeButton } from "../ui/widgets";
import { objProp, objClass } from "./BaseLevelScene";

/**
 * A menu/UI screen authored in a Tiled map instead of hardcoded in code. Text,
 * background images, and decor render via the shared Tiled renderer; `button`
 * objects (class `button`, with `label` + `action` custom properties) become
 * clickable buttons that start the scene named in `action`.
 *
 * Menu maps are a fixed 960x540 canvas (the game size) with a static camera.
 * Dynamic, data-driven content (e.g. Level Select's cards) still belongs in a
 * regular scene - this is for the mostly-static screens.
 */
export abstract class MenuScene extends Phaser.Scene {
  /** Buttons built from the current map (in map order), so a subclass can tweak them
   *  (e.g. the title screen reveals its Start button last). */
  protected menuButtons: Phaser.GameObjects.Container[] = [];

  /** Builds the menu and returns the authored text objects (in map order) so a
   *  subclass can tweak them (e.g. the title screen reveals its subtitle later). */
  protected buildMenu(mapKey: string): Phaser.GameObjects.Text[] {
    const map = this.make.tilemap({ key: mapKey });
    // addTilesetImage is only needed if the map places decor tiles, but it's
    // harmless otherwise and keeps decor working.
    if (map.tilesets[0]) {
      map.addTilesetImage(map.tilesets[0].name ?? TILESET_NAME, TILESET_KEY);
    }

    const texts = renderTiledAuthoredContent(this, map, mapKey);

    this.menuButtons = [];
    const layer = map.getObjectLayer("objects");
    for (const obj of layer?.objects ?? []) {
      if (objClass(obj) === "button") this.menuButtons.push(this.buildButton(obj));
    }
    return texts;
  }

  /** Build one button from a Tiled `button` object. The object's rectangle sets
   *  the button's size and centre; `label`/`action` come from its properties.
   *  Returns the button container so callers can reveal/animate it. */
  private buildButton(obj: Phaser.Types.Tilemaps.TiledObject): Phaser.GameObjects.Container {
    const label = objProp(obj, "label", "Button");
    const action = objProp(obj, "action", "");
    // Optional image-button art (label baked into the texture); see widgets.ts.
    const texture = objProp(obj, "texture", "");
    const hoverTexture = objProp(obj, "hoverTexture", "");
    const w = obj.width || 200;
    const h = obj.height || 48;
    const cx = (obj.x ?? 0) + w / 2;
    const cy = (obj.y ?? 0) + h / 2;
    return makeButton(
      this,
      cx,
      cy,
      label,
      () => {
        if (action) this.scene.start(action);
      },
      { width: w, height: h, texture: texture || undefined, hoverTexture: hoverTexture || undefined },
    ).setDepth(10);
  }
}
